import { z } from 'zod';

/**
 * Asset metadata contracts.
 *
 * Hard rule for the whole project: **nothing about an asset is inferred from
 * its file name**. Frame rate, scale, origin, animation ranges and hitbox are
 * always read from an explicit metadata record. Renaming a file must never
 * change gameplay or rendering.
 */

export const ASSET_CATEGORIES = [
  'ship',
  'cannon',
  'mob',
  'boss',
  'item',
  'artifact',
  /**
   * The five energy-system components.
   *
   * Its own category rather than `item`: a component ships TWO framings of one
   * design - the inventory icon, and the aligned layer that stacks into the
   * assembled machine - and the manifest has to be able to tell them apart.
   */
  'equipment',
  'pet',
  /**
   * **As entidades que uma carta põe na arena** (gate V28H.X).
   *
   * Categoria própria e não `pet`: um pet é do jogador, aparece no painel dele
   * e tem evoluções; isto é uma coisa que existe treze segundos dentro de um
   * duelo. As duas são renders 3D fotografados pelo mesmo pipeline, e é só isso
   * que têm em comum.
   */
  'summon',
  'skin',
  'projectile',
  'effect',
  'background',
  'ui',
  'audio',
  'brand',
] as const;

export type AssetCategory = (typeof ASSET_CATEGORIES)[number];
export const assetCategorySchema = z.enum(ASSET_CATEGORIES);

/** Stable, human-readable asset key. Independent from the file path. */
export const assetIdSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, 'asset id must be lower kebab/snake case');

/** Path relative to the asset root (R2 prefix in production). */
export const assetPathSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[a-z0-9][a-z0-9./_-]*$/, 'asset path must be lower case and relative');

/** Normalised anchor point. `{x:0.5,y:0.5}` is the sprite centre. */
export const originSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});

/**
 * Collision shape, expressed in *source pixels* of the frame.
 *
 * Rendering scale never changes the hitbox contract; the runtime applies the
 * same transform to both. Keeping them independent means an artist can hand in
 * a larger sprite without silently changing hit detection.
 */
export const hitboxSchema = z.discriminatedUnion('shape', [
  z.object({
    shape: z.literal('circle'),
    offsetX: z.number(),
    offsetY: z.number(),
    radius: z.number().positive(),
  }),
  z.object({
    shape: z.literal('rectangle'),
    offsetX: z.number(),
    offsetY: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
  }),
]);

export type Hitbox = z.infer<typeof hitboxSchema>;

/**
 * Mob and boss spritesheet layout.
 *
 * Fixed for this project: 3 columns x 3 rows, 9 frames, read row-major
 * left-to-right and top-to-bottom.
 *
 * Frames are *nominally* square, but several master sheets have dimensions not
 * divisible by three (e.g. 1024x999). The lossless pipeline splits them on
 * integer boundaries and pads to a common cell without resampling, so the
 * declared cell may legitimately be near-square (342x333) rather than exactly
 * square. Distorting the art to force squareness is forbidden; the schema
 * therefore does not require it.
 *
 * The values are still explicit fields (not constants baked into code) so a
 * future asset can declare a different grid without a code change, while the
 * schema keeps today's assets honest.
 */
export const spriteSheetLayoutSchema = z
  .object({
    columns: z.int().positive().default(3),
    rows: z.int().positive().default(3),
    frameWidth: z.int().positive(),
    frameHeight: z.int().positive(),
    /** Frames actually present, top-left first. */
    frameCount: z.int().positive().default(9),
    readingOrder: z.literal('row-major-ltr-ttb').default('row-major-ltr-ttb'),
  })
  .refine((layout) => layout.frameCount <= layout.columns * layout.rows, {
    message: 'frameCount exceeds the declared grid',
    path: ['frameCount'],
  });

export type SpriteSheetLayout = z.infer<typeof spriteSheetLayoutSchema>;

/** One named animation over a frame range of the sheet. */
export const animationSpecSchema = z.object({
  key: z.string().min(1).max(48),
  /** Frames per second. Per-asset, as required by the design. */
  frameRate: z.number().positive().max(120),
  /** `-1` loops forever. */
  repeat: z.int().min(-1).default(-1),
  /** Explicit frame indices; omit to use `0..frameCount-1`. */
  frames: z.array(z.int().min(0)).min(1).optional(),
  yoyo: z.boolean().default(false),
});

export type AnimationSpec = z.infer<typeof animationSpecSchema>;

const assetCommon = {
  id: assetIdSchema,
  file: assetPathSchema,
  /** Bumped whenever the art or the metadata changes; used for cache busting. */
  version: z.int().min(1).default(1),
  /** Render scale multiplier applied on top of any map/boss scaling. */
  scale: z.number().positive().default(1),
  origin: originSchema.default({ x: 0.5, y: 0.5 }),
  hitbox: hitboxSchema.optional(),
  /** Free-form note for the art pipeline. Never parsed by the game. */
  note: z.string().max(280).optional(),
};

/** Mobs and bosses: animated 3x3 spritesheets. */
export const animatedAssetSchema = z.object({
  ...assetCommon,
  kind: z.literal('animated'),
  category: z.enum(['mob', 'boss', 'effect', 'pet']),
  layout: spriteSheetLayoutSchema,
  animations: z.array(animationSpecSchema).min(1),
  defaultAnimation: z.string().min(1),
});

/** Ships, cannons, items, projectiles, UI: single static PNG with alpha. */
export const staticAssetSchema = z.object({
  ...assetCommon,
  kind: z.literal('static'),
  category: z.enum([
    'ship',
    'cannon',
    'item',
    'artifact',
    'equipment',
    // A pet's 2D form: one static PNG per evolution, alpha preserved. The 3D
    // models are not assets in this sense - they are geometry, and they live
    // outside the manifest entirely.
    'pet',
    'skin',
    'projectile',
    /*
     * A map's backdrop: one tile per map, seamless down its Y axis so it can
     * scroll forever. The category already existed in `ASSET_CATEGORIES`; the
     * static union simply never listed it, because until now nothing produced
     * one.
     */
    'background',
    'ui',
    'brand',
    /*
     * As duas peças do Leviatã Aéreo (gate V28H.X): a fuselagem e a hélice.
     *
     * Estáticas e não uma folha animada, porque só uma delas se mexe - e ela
     * mexe-se a rodar, que é uma coisa que a cena faz melhor do que uma
     * sequência de poses.
     */
    'summon',
  ]),
});

export const assetMetadataSchema = z.discriminatedUnion('kind', [
  animatedAssetSchema,
  staticAssetSchema,
]);

export type AnimatedAssetMetadata = z.infer<typeof animatedAssetSchema>;
export type StaticAssetMetadata = z.infer<typeof staticAssetSchema>;
export type AssetMetadata = z.infer<typeof assetMetadataSchema>;

/**
 * A map background is three parallax star layers plus up to three slow-moving
 * celestial bodies. The bodies descend slowly and may be randomly scaled at
 * spawn time, within the declared bounds.
 */
export const celestialBodySpecSchema = z.object({
  file: assetPathSchema,
  minScale: z.number().positive().default(0.6),
  maxScale: z.number().positive().default(1.6),
  /** Pixels per second, downwards. */
  driftSpeed: z.number().positive().default(6),
  opacity: z.number().min(0).max(1).default(0.8),
});

export const mapBackgroundSchema = z
  .object({
    mapId: z.string().regex(/^map-\d{2}$/, 'mapId must look like map-07'),
    /** Base colour behind every layer. The art direction is near-black. */
    baseColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    starLayers: z
      .array(
        z.object({
          density: z.int().positive().max(2000),
          speed: z.number().positive(),
          size: z.number().positive(),
          color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
          alpha: z.number().min(0).max(1).default(1),
        }),
      )
      .min(1),
    /** Exactly three slots are reserved per map; files may arrive later. */
    celestialBodies: z.array(celestialBodySpecSchema).max(3).default([]),
  })
  .refine((background) => background.celestialBodies.every((b) => b.minScale <= b.maxScale), {
    message: 'minScale must not exceed maxScale',
    path: ['celestialBodies'],
  });

export type MapBackground = z.infer<typeof mapBackgroundSchema>;

/** Full manifest shipped alongside the build (later: served from R2). */
export const assetManifestSchema = z.object({
  manifestVersion: z.int().min(1),
  /** Base URL or R2 prefix the `file` paths are resolved against. */
  baseUrl: z.string(),
  assets: z.array(assetMetadataSchema),
  backgrounds: z.array(mapBackgroundSchema).default([]),
});

export type AssetManifest = z.infer<typeof assetManifestSchema>;
